import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { gsap, ScrollTrigger } from '../foundation/animation.js';

export function initThreeJSBackground() {
  const threejsContainer = document.getElementById('threejs-container');
  const canvas = document.getElementById('threejs-canvas');

  if (!threejsContainer || !canvas) {
    console.warn('Three.js container or canvas not found. Skipping initialization.');
    return;
  }

  let canRotate = false; // 回転を制御するフラグ
  let textSphere;
  let isIntersecting = true; // 表示領域判定フラグ

  // シーン
  const scene = new THREE.Scene();
  const getContainerWidth = () => threejsContainer.clientWidth || window.innerWidth;
  const getContainerHeight = () => threejsContainer.clientHeight || window.innerHeight;

  const camera = new THREE.PerspectiveCamera(
    60,
    getContainerWidth() / getContainerHeight(),
    0.1,
    800
  );
  camera.position.set(0, 80, 280); // 角度を少し下げる（上すぎないように）
  camera.lookAt(0, 0, 0);

  const renderer = new THREE.WebGLRenderer({
    antialias: false,
    canvas: canvas,
    powerPreference: 'low-power',
    stencil: false
  });
  renderer.setClearColor(0xffffff);
  // デバイスピクセル比に合わせてレンダラーを高解像度にする
  // パフォーマンス向上のため最大2までに制限
  const DPR = Math.min(window.devicePixelRatio || 1, 2);
  renderer.setPixelRatio(DPR);
  // CSS サイズも更新して描画バッファとスタイルのズレを防ぐ
  renderer.setSize(getContainerWidth(), getContainerHeight());

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableZoom = false;

  // 文字スプライトを作る関数
  function createCharSprite(text) {
    // HiDPI対応でオフスクリーンキャンバスを作る（外側の canvas と名前を被らせない）
    const offscreenCanvas = document.createElement('canvas');
    const ctx = offscreenCanvas.getContext('2d');
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    const baseFontSize = 12; // CSSpx ベース（見た目調整）
    const fontFamily = 'Fragment Mono';
    const fontSpec = `${baseFontSize * DPR}px ${fontFamily}`;

    // レイアウト用に一度フォントをセットして幅を測る
    ctx.font = fontSpec;
    const textMetrics = ctx.measureText(text);
    const textWidthCss = Math.ceil(textMetrics.width);
    const paddingCss = 5;

    // キャンバスを DPR 倍で確保
    offscreenCanvas.width = (textWidthCss + paddingCss * 2) * DPR;
    offscreenCanvas.height = (baseFontSize * 3) * DPR;

    // スケールして通常のCSS座標系で描画できるようにする
    ctx.scale(DPR, DPR);
    ctx.font = `${baseFontSize}px ${fontFamily}`; // CSSpx を使う（scale が適用済み）
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const cx = (offscreenCanvas.width / DPR) / 2;
    const cy = (offscreenCanvas.height / DPR) / 2;
    ctx.fillText(text, cx, cy);

    const texture = new THREE.CanvasTexture(offscreenCanvas);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = false;
    texture.needsUpdate = true;

    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      opacity: 0, // 初期状態は透明
      side: THREE.FrontSide, // 裏面の文字（反転して見える）を非表示にする
      depthWrite: false // 透明部分が奥のオブジェクトを隠さないようにする
    });
    const geometry = new THREE.PlaneGeometry(1, 1);
    const mesh = new THREE.Mesh(geometry, material);

    // 表示ピクセル高さを指定（必要に応じて調整）
    const desiredPixelHeight = 20; // ← ここを小さくすると見た目の文字が小さくなる

    // テクスチャのアスペクト（CSSピクセル比）
    const aspect = (offscreenCanvas.width / DPR) / (offscreenCanvas.height / DPR);

    // ユーザーデータに保存して、リサイズ時に再計算する
    mesh.userData = {
      aspect,
      desiredPixelHeight
    };

    // 初期スケールはダミー
    mesh.scale.set(1 * aspect, 1, 1);
    return mesh;
  }

  // 表示する単語配列（IGNITION POINT FORCE関連キーワード）
  const wordArray = [
    "Force", "Firm", "Fast Forward", "Full Stack",
    "IGNITION POINT FORCE.Inc", "product", "development",
    "professional", "talent", "matching", "system", "integration",
    "technology", "consulting", "Communication", "Innovation", "Enjoy",
  ];

  // パフォーマンス向上のため、単語ごとにスプライトをキャッシュ
  const spriteCache = new Map();
  const initSprites = () => {
    wordArray.forEach(word => {
      spriteCache.set(word, createCharSprite(word));
    });
  };

  // 球体に文字を配置する関数
  function createDenseTextSphere(textArray, radius, rings, segments) {
    const group = new THREE.Group();
    let charIndex = 0;

    for (let i = 0; i <= rings; i++) {
      const phi = (i * Math.PI) / rings; // 緯度
      // 緯度に応じてセグメント数を調整し、極での密集を避ける
      const segmentsForRing = Math.max(1, Math.floor(Math.sin(phi) * segments));

      // 各段の開始角度をずらすことで、上下の単語と互い違いにする（ジグザグ配置）
      const offsetTheta = (i % 2) * (Math.PI / segmentsForRing);

      for (let j = 0; j < segmentsForRing; j++) {
        // 経度方向の間隔を均等に保ちつつ、オフセットを適用
        const theta = (j * 2 * Math.PI) / segmentsForRing + offsetTheta;

        const text = textArray[charIndex % textArray.length];

        // キャッシュからスプライトをクローンして再利用
        const originalMesh = spriteCache.get(text);
        const mesh = originalMesh.clone();
        // マテリアルもクローンしないと、opacityアニメーションが全スプライトで共有されてしまう
        mesh.material = originalMesh.material.clone();
        // userDataもクローン
        mesh.userData = { ...originalMesh.userData };

        // 球体座標変換
        const x = radius * Math.sin(phi) * Math.cos(theta);
        const y = radius * Math.cos(phi);
        const z = radius * Math.sin(phi) * Math.sin(theta);
        mesh.position.set(x, y, z);

        // 円に沿わせる（中心から外側を向くように回転）
        mesh.lookAt(new THREE.Vector3(x * 2, y * 2, z * 2));
        group.add(mesh);

        // 裏側からも読めるように、内向きのメッシュも追加（BackfaceCullingで表裏を切り替え）
        // Z-fighting（ちらつき）防止のため、わずかに内側に配置
        const meshIn = originalMesh.clone();
        meshIn.material = originalMesh.material.clone();
        meshIn.userData = { ...originalMesh.userData };

        const innerRadiusScale = 0.995; // わずかに半径を小さくする
        meshIn.position.set(x * innerRadiusScale, y * innerRadiusScale, z * innerRadiusScale);

        meshIn.lookAt(new THREE.Vector3(0, 0, 0));
        group.add(meshIn);

        charIndex++;
      }
    }
    return group;
  }

  // スプライトのスケールを一括更新する関数
  function updateSpriteScales() {
    if (!textSphere) return;
    const canvasHeight = renderer.domElement.clientHeight || window.innerHeight;
    const vFOV = THREE.MathUtils.degToRad(camera.fov);

    // 基準距離（初期カメラ位置z=300、球中心(0,0,0)への距離）
    const distance = camera.position.distanceTo(new THREE.Vector3(0, 0, 0));
    const worldScreenHeight = 2 * distance * Math.tan(vFOV / 2);
    const worldPerPixel = worldScreenHeight / canvasHeight;

    textSphere.children.forEach(sprite => {
      if (!sprite.userData) return;
      const { aspect, desiredPixelHeight } = sprite.userData;
      // ワールド座標系での高さを計算
      const worldHeight = desiredPixelHeight * worldPerPixel;
      const worldWidth = worldHeight * aspect;
      sprite.scale.set(worldWidth, worldHeight, 1);
    });
  }

  // 球体に配置（rings, segments を増やすと密度UP）
  // パフォーマンスチューニング: 以前の (100, 12, 20) からスプライト数を削減して軽量化
  // (100, 10, 16) 程度に抑える

  document.fonts.ready.then(() => {
    initSprites();
    // 横方向の間隔を確保しつつ配置。
    // segmentsを控えめ(20)に設定し、横の単語同士の距離を離す
    textSphere = createDenseTextSphere(wordArray, 100, 24, 20);
    scene.add(textSphere);

    // 初期スケール計算
    updateSpriteScales();

    if (threejsContainer.classList.contains('l-background--company')) {
      // コンテナサイズに基づいてスケールを調整
      const updateScale = () => {
        const baseScale = window.innerWidth < 768 ? 0.66 : 0.9;
        textSphere.scale.set(baseScale, baseScale, baseScale);
      };
      updateScale();
      window.addEventListener('resize', updateScale);
    }
  });


  // IntersectionObserverによる表示制御（画面外ではレンダリング停止）
  const io = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      isIntersecting = entry.isIntersecting;
    });
  }, { rootMargin: '200px' }); // 少し余裕を持って描画開始
  io.observe(threejsContainer);

  let animationFrameId = null;

  let lastTime = 0;

  // アニメーション
  function animate(time) {
    animationFrameId = requestAnimationFrame(animate);

    if (!time) time = performance.now();

    // 画面外または準備前の場合は描画しない
    if (!textSphere || !isIntersecting) return;

    if (lastTime === 0) {
      lastTime = time;
    }

    const delta = time - lastTime;
    lastTime = time;

    if (canRotate) {
      // 60fps(約16.66ms)で0.003回転していたのを基準とする
      const factor = delta / 16.66;
      // タブ復帰時などに回転が飛びすぎないよう制限
      const safeFactor = Math.min(factor, 5);
      textSphere.rotation.y += 0.003 * safeFactor;
    }

    // レンダリングループ内での重いスケール計算を削除
    renderer.render(scene, camera);
  }
  animate();

  // ウィンドウサイズ変更対応
  window.addEventListener("resize", () => {
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    renderer.setPixelRatio(DPR);
    camera.aspect = getContainerWidth() / getContainerHeight();
    camera.updateProjectionMatrix();
    // CSSサイズも更新
    renderer.setSize(getContainerWidth(), getContainerHeight());

    // リサイズ時にスケールを再計算
    updateSpriteScales();
  });

  // 表示アニメーション
  function introAnimation() {
    if (!textSphere) {
      // textSphere がまだ作成されていない場合は、作成を待ってから実行
      document.fonts.ready.then(() => {
        introAnimation();
      });
      return;
    }
    const sprites = [...textSphere.children];
    // 配列をシャッフルしてランダムに見せる
    for (let i = sprites.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [sprites[i], sprites[j]] = [sprites[j], sprites[i]];
    }

    const tl = gsap.timeline({
      onComplete: () => {
        canRotate = true; // アニメーション完了後に回転を許可
      }
    });

    // 5段階に分けて表示
    const batchSize = Math.ceil(sprites.length / 5);
    for (let i = 0; i < 5; i++) {
      const batch = sprites.slice(i * batchSize, (i + 1) * batchSize);
      if (batch.length > 0) {
        tl.to(batch.map(s => s.material), {
          opacity: 1,
          duration: 0.2,
          ease: 'power1.inOut'
        }, i * 0.1); // 各段階を0.1秒ずらす
      }
    }
  }

  // bodyのclass変更を監視
  const observer = new MutationObserver((mutationsList, observer) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        if (document.body.classList.contains('is-animation-ready')) {
          introAnimation();
          observer.disconnect(); // 目的のクラスがついたら監視を終了
          break;
        }
      }
    }
  });

  // 監視を開始
  observer.observe(document.body, { attributes: true });

  // 既にクラスが存在する場合にも対応
  if (document.body.classList.contains('is-animation-ready')) {
    introAnimation();
    observer.disconnect();
  }


  // ScrollTriggerアニメーション
  if (document.querySelector('.p-home-keyvisual')) {
    gsap.to(camera.position, {
      z: -100, // 拡大後のz座標
      y: -29, // 中心を通る軌道にする（(0,80,280) -> (0,0,0) -> (0,-29,-100)）
      scrollTrigger: {
        trigger: ".p-home-keyvisual", // トリガーとなる要素
        start: "bottom bottom", // トリガー要素のbottomがビューポートのbottomに到達したときに開始
        endTrigger: ".l-main", // 終了トリガーをl-mainに設定
        end: "top 20%", // l-mainの上が画面上から20%の位置に来た時に終了
        scrub: 1, // スクロールに連動
        // markers: true // デバッグ用
      },
      onUpdate: () => {
        // 描画がスキップされないように、スクロール時は強制的に描画が必要な場合があるが、
        // ScrollTriggerはスクロール時に発火するので、animateループが回っていれば反映される。
        // 画面外ならanimateが止まるが、スクロールしているということは画面内にある可能性が高い。

        const z = camera.position.z;
        const startZ = -30;
        const endZ = -100;

        if (z < startZ && z >= endZ) {
          // Map z from [startZ, endZ] to a progress value [0, 1]
          const progress = (z - startZ) / (endZ - startZ);
          const blur = progress * 10; // Max blur 5px
          const opacity = 1 - progress;
          canvas.style.filter = `blur(${blur}px)`;
          canvas.style.opacity = opacity;
        } else if (z >= startZ) {
          // Reset if we scroll back up
          canvas.style.filter = 'none';
          canvas.style.opacity = 1;
        } else {
          // Keep it blurred and transparent at the end
          canvas.style.filter = 'blur(10px)';
          canvas.style.opacity = 0;
        }
      }
    });
  }
}
